import type { ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import {
  LARGE_PREVIEW_CONTEXT_THEME,
  LARGE_PREVIEW_GRID_CLUE,
  isLargePreviewGalleryMode,
  type LargePreviewClueTheme,
} from './notePreviewUi';
import type { LargePreviewKeyboardContext } from './useLargePreviewMediaKeyboard';

type LargePreviewKeyboardClueProps = {
  clue: LargePreviewClueTheme;
};

export function LargePreviewKeyboardClue({ clue }: LargePreviewKeyboardClueProps) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        gap: 0.75,
        pl: 1.5,
        ml: 0.5,
        borderLeft: '3px solid',
        borderColor: clue.border,
      }}
      role="status"
      aria-live="polite"
    >
      <Typography
        component="span"
        variant="body2"
        sx={{ fontWeight: 700, color: clue.accent, lineHeight: 1.3 }}
      >
        {clue.title}
      </Typography>
      <Typography component="span" variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }}>
        {clue.detail}
      </Typography>
    </Box>
  );
}

function resolveClue(previewMode: string, keyboardContext: LargePreviewKeyboardContext): LargePreviewClueTheme | null {
  if (!isLargePreviewGalleryMode(previewMode)) {
    return null;
  }
  if (previewMode === 'grid') {
    return LARGE_PREVIEW_GRID_CLUE;
  }
  if (keyboardContext === 'off') {
    return LARGE_PREVIEW_CONTEXT_THEME.gallery;
  }
  return LARGE_PREVIEW_CONTEXT_THEME[keyboardContext];
}

export type LargePreviewMode = 'rendered' | 'raw' | 'media' | 'grid';

export function LargePreviewModeToolbar({
  mode,
  onModeChange,
  showMediaGrid,
}: {
  mode: string;
  onModeChange: (mode: LargePreviewMode) => void;
  showMediaGrid: boolean;
}) {
  return (
    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, ml: { xs: 0, sm: 'auto' } }}>
      {showMediaGrid && (
        <Button
          size="small"
          variant={mode === 'media' ? 'contained' : 'outlined'}
          onClick={() => onModeChange('media')}
        >
          Media
        </Button>
      )}
      {showMediaGrid && (
        <Button
          size="small"
          variant={mode === 'grid' ? 'contained' : 'outlined'}
          onClick={() => onModeChange('grid')}
        >
          Grid
        </Button>
      )}
      <Button
        size="small"
        variant={mode === 'rendered' ? 'contained' : 'outlined'}
        onClick={() => onModeChange('rendered')}
      >
        Rendered
      </Button>
      <Button
        size="small"
        variant={mode === 'raw' ? 'contained' : 'outlined'}
        onClick={() => onModeChange('raw')}
      >
        Raw HTML
      </Button>
    </Box>
  );
}

/** Title row: "Preview" + clue (left) and mode toolbar (right). */
export function LargePreviewDialogTitle({
  previewMode,
  keyboardContext,
  modeToolbar,
}: {
  previewMode: string;
  keyboardContext: LargePreviewKeyboardContext;
  modeToolbar?: ReactNode;
}) {
  const clue = resolveClue(previewMode, keyboardContext);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1.5,
        width: '100%',
        pr: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, minWidth: 0 }}>
        <Typography component="span" variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          Preview
        </Typography>
        {clue && <LargePreviewKeyboardClue clue={clue} />}
      </Box>
      {modeToolbar}
    </Box>
  );
}
